import type { CallLogDto, CallLogDirection } from "@/services/api";
import { initialsFromName } from "@/lib/media";
import type { CallDirection, CallEvent, CallRecord } from "./types";

function normalizePhoneDigits(value: string) {
  return value.replace(/\D/g, "");
}

function formatDuration(seconds?: number) {
  const s = Math.max(0, Number(seconds) || 0);
  if (s <= 0) return undefined;
  const mins = Math.floor(s / 60);
  const secs = s % 60;
  if (mins <= 0) return `${secs}s`;
  return `${mins}m ${String(secs).padStart(2, "0")}s`;
}

export function formatCallTime(iso: string) {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";

  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfYesterday = new Date(startOfToday);
  startOfYesterday.setDate(startOfYesterday.getDate() - 1);

  const time = date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  if (date >= startOfToday) return time;
  if (date >= startOfYesterday) return "Yesterday";
  return date.toLocaleDateString([], { month: "short", day: "numeric" });
}

function directionLabel(direction: CallDirection) {
  if (direction === "missed") return "Missed voice call";
  if (direction === "incoming") return "Incoming voice call";
  return "Outgoing voice call";
}

function statusLabel(log: CallLogDto) {
  if (log.direction === "missed" || !log.answered) return "Unanswered";
  const duration = formatDuration(log.duration_seconds);
  return duration ? `Answered · ${duration}` : "Answered";
}

function toEvent(log: CallLogDto): CallEvent {
  return {
    id: String(log.id),
    direction: log.direction,
    label: directionLabel(log.direction),
    time: formatCallTime(log.started_at),
    status: statusLabel(log),
    startedAt: log.started_at,
  };
}

export function groupCallLogs(
  logs: CallLogDto[],
  contacts: Array<{ name: string; phone?: string | null; favourite?: boolean }> = [],
): CallRecord[] {
  const byNumber = new Map<string, CallLogDto[]>();

  for (const log of logs) {
    const key = normalizePhoneDigits(log.number) || log.number.trim() || "unknown";
    const bucket = byNumber.get(key);
    if (bucket) bucket.push(log);
    else byNumber.set(key, [log]);
  }

  const records: CallRecord[] = [];
  for (const [key, entries] of byNumber) {
    const sorted = [...entries].sort(
      (a, b) => Date.parse(b.started_at) - Date.parse(a.started_at),
    );
    const latest = sorted[0]!;
    const contact = contacts.find(
      (item) => normalizePhoneDigits(item.phone || "") === key,
    );
    // Prefer directory contact name when the number is already saved.
    const name =
      contact?.name ||
      (latest.display_name && latest.display_name !== latest.number
        ? latest.display_name
        : null) ||
      latest.display_name ||
      latest.number;

    records.push({
      id: key,
      name,
      number: latest.number,
      direction: latest.direction,
      time: formatCallTime(latest.started_at),
      duration: formatDuration(latest.duration_seconds),
      initials: initialsFromName(name),
      favourite: Boolean(contact?.favourite),
      hasContact: Boolean(contact),
      history: sorted.map(toEvent),
    });
  }

  return records.sort((a, b) => {
    const aTime = Date.parse(a.history[0]?.startedAt || "") || 0;
    const bTime = Date.parse(b.history[0]?.startedAt || "") || 0;
    return bTime - aTime;
  });
}

export function filterCallRecords(
  records: CallRecord[],
  direction: CallLogDirection | "all",
  query: string,
) {
  const q = query.trim().toLowerCase();
  return records.filter((item) => {
    if (direction !== "all" && item.direction !== direction) return false;
    if (!q) return true;
    return (
      item.name.toLowerCase().includes(q) ||
      item.number.toLowerCase().includes(q) ||
      item.number.replace(/\D/g, "").includes(q.replace(/\D/g, ""))
    );
  });
}
